/**
 * In-app banner: the small glass card that slides down from the top when something arrives while the app is open
 * (a push in the foreground). One at a time; a newer banner replaces the current one. Rendered by
 * `InAppBanner.vue` (mounted once in App.vue).
 *
 *   showInAppBanner({ title: 'Nike reel', subtitle: 'Waiting for your OK', body: '…', onOpen: () => go() })
 */
import { readonly, shallowRef } from 'vue'

export interface InAppBannerOptions {
    title: string
    /** One short line above the body ("Waiting for your OK"). */
    subtitle?: string
    body?: string
    /** `attention`: the subtitle takes the accent colour (approvals, questions). */
    tone?: 'default' | 'attention'
    /** Tapping the banner. Without it the banner only dismisses. */
    onOpen?: () => void
    /** Auto-dismiss after this long. Default 6s. */
    durationMs?: number
}

export interface InAppBannerEntry extends InAppBannerOptions {
    id: number
}

export const IN_APP_BANNER_MS = 6000

const current = shallowRef<InAppBannerEntry | null>(null)
let seq = 0

export function showInAppBanner(options: InAppBannerOptions): number {
    const id = ++seq
    current.value = { ...options, id }
    return id
}

/** Dismiss the banner (only if it is still `id`, when given). */
export function dismissInAppBanner(id?: number): void {
    if (id !== undefined && current.value?.id !== id) return
    current.value = null
}

export function useInAppBanner() {
    return { current: readonly(current) }
}
